/**
 * Primitives géométriques du module d'analyse — pures, partagées entre le
 * regroupement (`grouping.ts`), la passe photos (`photo-assignment.ts`) et
 * le calcul des cadres à l'écran.
 *
 * D'abord les bbox (liaison des tracés, cadres), puis la géométrie RÉELLE :
 * distance à une polyligne, intérieur d'un polygone, enveloppe convexe, cône
 * de visée. C'est ce qui évite de confondre un trait en diagonale avec le
 * rectangle vide qui l'entoure.
 */

import type { Point, WorldBbox } from "./types";

/** Union de deux bbox. */
export function bboxUnion(a: WorldBbox, b: WorldBbox): WorldBbox {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

/** Diagonale d'une bbox (0 pour un point). */
export function bboxDiagonal(b: WorldBbox): number {
  return Math.hypot(b.maxX - b.minX, b.maxY - b.minY);
}

/**
 * Écart entre deux bbox : distance entre leurs bords les plus proches,
 * 0 si elles se chevauchent. C'est la métrique de proximité spatiale du
 * regroupement (plus stable que la distance entre centres, qui pénaliserait
 * les grands tracés).
 */
export function bboxGap(a: WorldBbox, b: WorldBbox): number {
  const dx = Math.max(0, a.minX - b.maxX, b.minX - a.maxX);
  const dy = Math.max(0, a.minY - b.maxY, b.minY - a.maxY);
  return Math.hypot(dx, dy);
}

export function bboxCenter(b: WorldBbox): Point {
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
}

/** Coins d'une bbox, en contour fermé (ordre trigonométrique). */
export function bboxCorners(b: WorldBbox): Point[] {
  return [
    { x: b.minX, y: b.minY },
    { x: b.maxX, y: b.minY },
    { x: b.maxX, y: b.maxY },
    { x: b.minX, y: b.maxY },
  ];
}

export function distance(a: Point, b: Point): number {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

/** Distance d'un point au segment [a, b]. */
export function pointSegmentDistance(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return distance(p, a);
  const t = Math.max(
    0,
    Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / len2),
  );
  return distance(p, { x: a.x + t * dx, y: a.y + t * dy });
}

/** Intérieur d'un polygone (lancer de rayon pair/impair) ; faux si dégénéré. */
export function pointInPolygon(p: Point, polygon: Point[]): boolean {
  if (polygon.length < 3) return false;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i];
    const b = polygon[j];
    if (a.y > p.y === b.y > p.y) continue;
    const x = a.x + ((p.y - a.y) * (b.x - a.x)) / (b.y - a.y);
    if (p.x < x) inside = !inside;
  }
  return inside;
}

/**
 * Distance d'un point à une polyligne — 0 à l'intérieur d'un contour fermé
 * (une hachure « contient » le point). Infinity pour une polyligne vide.
 */
export function pointPolylineDistance(
  p: Point,
  points: Point[],
  closed: boolean,
): number {
  if (points.length === 0) return Number.POSITIVE_INFINITY;
  if (points.length === 1) return distance(p, points[0]);
  if (closed && pointInPolygon(p, points)) return 0;
  let best = Number.POSITIVE_INFINITY;
  const count = closed ? points.length : points.length - 1;
  for (let i = 0; i < count; i++) {
    const d = pointSegmentDistance(
      p,
      points[i],
      points[(i + 1) % points.length],
    );
    if (d < best) best = d;
  }
  return best;
}

/**
 * Enveloppe convexe (chaîne monotone d'Andrew), sommets en ordre
 * trigonométrique, sans doublons. Moins de 3 points distincts → les points
 * eux-mêmes (segment ou point : pas d'intérieur).
 */
export function convexHull(points: Point[]): Point[] {
  const sorted = [...points]
    .sort((a, b) => a.x - b.x || a.y - b.y)
    .filter((p, i, arr) => i === 0 || p.x !== arr[i - 1].x || p.y !== arr[i - 1].y);
  if (sorted.length < 3) return sorted;
  const cross = (o: Point, a: Point, b: Point): number =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: Point[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}

/** Vecteur unitaire de `from` vers `to`, null s'ils sont confondus. */
export function unitVector(from: Point, to: Point): Point | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return null;
  return { x: dx / len, y: dy / len };
}

/**
 * Premier contact d'un CÔNE de visée avec une polyligne : distance AVANT
 * (projection sur la direction) du point de la polyligne le plus proche de
 * l'origine parmi ceux situés dans le cône (demi-angle, portée). Null si la
 * polyligne n'est pas visée. Les segments sont échantillonnés finement (pas
 * ≤ portée/40) : une flèche courte et imprécise « touche » ce qu'elle désigne
 * approximativement, y compris entre deux sommets.
 */
export function coneHitDistance(
  origin: Point,
  direction: Point,
  halfAngleRad: number,
  reach: number,
  points: Point[],
  closed: boolean,
): number | null {
  if (points.length === 0 || reach <= 0) return null;
  const cosLimit = Math.cos(halfAngleRad);
  const step = reach / 40;
  let best: number | null = null;
  const probe = (q: Point): void => {
    const vx = q.x - origin.x;
    const vy = q.y - origin.y;
    const forward = vx * direction.x + vy * direction.y;
    if (forward <= 0) return;
    const dist = Math.hypot(vx, vy);
    if (dist > reach || forward / dist < cosLimit) return;
    if (best === null || forward < best) best = forward;
  };
  probe(points[0]);
  const count =
    points.length === 1 ? 0 : closed ? points.length : points.length - 1;
  for (let i = 0; i < count; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const n = Math.min(24, Math.max(1, Math.ceil(distance(a, b) / step)));
    for (let k = 1; k <= n; k++) {
      const t = k / n;
      probe({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
    }
  }
  return best;
}
